import { useMutation } from '@apollo/client'

import { useState } from 'react'

import { Toaster } from '@grupoboticario/flora-react'

import { ERROR_MESSAGES, SUCCESS_MESSAGES, REDIRECT_DELAY } from '@/constants/eventMessages'
import { CREATE_UPDATE_EVENT } from '@/graphql/mutation/event'
import { prepareEventForSubmit } from '@/utils/events/prepareEventData'

/**
 * Hook to handle submit and persistence of event data.
 *
 * @param {object} params - Hook parameters.
 * @param {object} params.formValues - Event form data.
 * @param {string} params.eventId - Event ID.
 * @param {boolean} params.isNewEvent - Indicates if it is a new event.
 * @param {function} params.push - Next.js navigation function.
 * @returns {object} { isUploading, isSaving, handleSubmit }
 */
export function useEventSubmit({ formValues, eventId, isNewEvent, push }) {
  const [isUploading, setIsUploading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [createUpdateEvent] = useMutation(CREATE_UPDATE_EVENT)

  const saveEvent = async (eventData, refetchBenefitEvents) => {
    try {
      setIsSaving(true)
      const { data, errors } = await createUpdateEvent({ variables: { event: eventData } })
      if (errors || data?.createUpdateEvent?.error) {
        throw new Error(data?.createUpdateEvent?.error || ERROR_MESSAGES.UNKNOWN_ERROR)
      }
      Toaster.notify({
        kind: 'success',
        description: isNewEvent ? SUCCESS_MESSAGES.CREATE_EVENT_SUCCESS : SUCCESS_MESSAGES.UPDATE_EVENT_SUCCESS
      })
      if (refetchBenefitEvents) await refetchBenefitEvents()
      setTimeout(() => {
        push('/benefits/events')
      }, REDIRECT_DELAY)
    } catch {
      Toaster.notify({
        kind: 'error',
        description: isNewEvent ? ERROR_MESSAGES.CREATE_EVENT_ERROR : ERROR_MESSAGES.UPDATE_EVENT_ERROR
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleSubmit = async (e, refetchBenefitEvents) => {
    e?.preventDefault()
    try {
      setIsUploading(true)
      const eventData = await prepareEventForSubmit(formValues, eventId, isNewEvent)
      await saveEvent(eventData, refetchBenefitEvents)
    } catch {
      Toaster.notify({
        kind: 'error',
        description: ERROR_MESSAGES.PREPARE_EVENT_ERROR
      })
    } finally {
      setIsUploading(false)
    }
  }

  return {
    isUploading,
    isSaving,
    handleSubmit
  }
}

export default useEventSubmit
